import { graphqlRequest } from './graphqlRequest';
import {
  checkedTasks,
  scopeGroups,
  selectionPrice,
  termRowsFromCatalog,
} from '../pages/ProfilePages/components/quoteCatalog';
import { tierAmount, tiersOf } from '../pages/ServiceDashboard/components/serviceCatalog';

/**
 * A "pick" is one catalog service chosen for a quote / invoice / project:
 * { catalogId, code, name, tierName, tierCode, unitPrice, pricedByTasks, tasks, termRows }
 * where tasks carry { title, scope, instructions, groupName, price, ... }.
 */

export const CATALOG_SERVICES = `
  query CatalogServices {
    getService(includeStats: false) {
      id
      service
      description
      serviceId
      status
      category
      catalog
    }
  }
`;

export async function fetchCatalogServices() {
  const data = await graphqlRequest(CATALOG_SERVICES);
  return (data?.getService || []).filter(
    (row) => String(row.status || 'ACTIVE').toUpperCase() !== 'INACTIVE',
  );
}

/** Build a pick from a selection made in the catalog picker. */
export function buildPick(service, tier, groups, checked) {
  const tasks = checkedTasks(groups, checked, service);
  return {
    bookingServiceId: '',
    catalogId: service.id,
    code: service.serviceId || '',
    name: service.service,
    category: String(service.category || '').trim(),
    tierName: tier?.name || '',
    tierCode: tier?.code || '',
    unitPrice: selectionPrice(tier, groups, checked),
    pricedByTasks:
      !(tier && tierAmount(tier) > 0) && tasks.some((task) => Number(task.price) > 0),
    tasks,
    termRows: termRowsFromCatalog(service),
  };
}

/** Default pick for a service + tier name (all default tasks ticked), e.g. from a lead's service row. */
export function pickFromCatalog(service, tierName = '', price = 0) {
  const tier =
    tiersOf(service).find(
      (row) => String(row.name || '').trim() === String(tierName || '').trim(),
    ) || null;
  const groups = scopeGroups(service, tier);
  const checked = {};
  groups.forEach((group) =>
    (group.tasks || []).forEach((task) => {
      if (task?.id) checked[task.id] = task.includedByDefault !== false;
    }),
  );
  const pick = buildPick(service, tier, groups, checked);
  return Number(price) > 0 ? { ...pick, unitPrice: Number(price) } : pick;
}

/** Project createProject `serviceLines`: the same shape an accepted quote snapshots. */
export function picksToServiceLines(picks, frequency) {
  const cadence = String(frequency || '').trim();
  return (picks || []).map((pick) => {
    const byTask = Boolean(pick.pricedByTasks);
    const amount = Number(pick.unitPrice) || 0;
    return {
      catalogServiceId: pick.catalogId,
      name: pick.name,
      tierName: pick.tierName || '',
      linePricingMode: byTask ? 'task' : 'service',
      price: amount,
      discount: 0,
      total: amount,
      tasks: (pick.tasks || []).map((task) => {
        const price = byTask ? Number(task.price) || 0 : 0;
        return {
          title: task.title,
          description: task.scope || '',
          scope: task.scope || '',
          instructions: task.instructions || '',
          groupName: task.groupName || '',
          requirement: task.requirement,
          assignedRole: task.assignedRole,
          frequency: task.frequency,
          price,
          discount: 0,
          total: price,
        };
      }),
      ...(cadence ? { frequency: cadence } : {}),
    };
  });
}

const EXISTING = `
  query PickBookingServices($bookingId: String!) {
    findBookingService(bookingId: $bookingId) {
      data { id tier service { id } }
    }
  }
`;

const REPRICE = `
  mutation RepricePickService($id: String!, $price: Float!, $tier: String) {
    updateBookingService(
      id: $id
      updateServiceInput: { price: $price, total: $price, tier: $tier }
    ) { id }
  }
`;

const ADD = `
  mutation AddPickService($booking: String!, $service: String!, $price: Float!, $tier: String) {
    createBookingServices(
      data: { input: [{ booking: $booking, service: $service, price: $price, discount: 0,
        frequency: "One-time", total: $price, tier: $tier }] }
    ) { id }
  }
`;

/**
 * Put each pick on the lead's services: one row per catalog service. If the
 * service is already there, reuse that row with the new price and tier.
 * Returns row ids in order.
 */
export async function saveBookingServicesForPicks(bookingId, picks) {
  const existing = await graphqlRequest(EXISTING, { bookingId });
  const rows = existing?.findBookingService?.data || [];
  const ids = [];
  for (const pick of picks || []) {
    const tier = pick.tierName || null;
    const price = Number(pick.unitPrice) || 0;
    const match = rows.find((row) => row.service?.id === pick.catalogId);
    if (match) {
      await graphqlRequest(REPRICE, { id: match.id, price, tier }, { timeout: 60000 });
      ids.push(match.id);
    } else {
      const res = await graphqlRequest(
        ADD,
        { booking: bookingId, service: pick.catalogId, price, tier },
        { timeout: 60000 },
      );
      const id = res?.createBookingServices?.[0]?.id;
      if (!id) throw new Error('The service was not added');
      rows.push({ id, tier, service: { id: pick.catalogId } });
      ids.push(id);
    }
  }
  return ids;
}
